import React from 'react';

export default function TermsOfService({ onBack }) {
  const lastUpdated = 'April 28, 2026';
  const companyName = 'TheClearAudit';
  const companyEmail = 'theclearaudit@gmail.com';
  const websiteUrl = 'https://theclearaudit.com';

  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `By accessing or using ${companyName} ("the Service", "the Platform", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. These Terms apply to all users of the Service including individuals, companies, and organizations.`,
    },
    {
      title: '2. Description of Service',
      content: `${companyName} is a cybersecurity compliance preparation platform designed to help defense contractors prepare for Cybersecurity Maturity Model Certification (CMMC) assessments. The Service provides tools including self-assessment questionnaires, gap analysis, remediation task tracking, evidence management, document generation, and AI-assisted guidance.\n\nThe Service is intended as a preparation and organizational tool only. It does not replace professional cybersecurity advice, legal counsel, or official CMMC certification processes.`,
    },
    {
      title: '3. IMPORTANT DISCLAIMER — NOT AN OFFICIAL CMMC ASSESSMENT TOOL',
      content: `THECLEARAUDIT IS NOT AN OFFICIAL CMMC ASSESSMENT TOOL AND DOES NOT CERTIFY ORGANIZATIONS.\n\nUsing this Service does not constitute CMMC certification, does not guarantee compliance with CMMC requirements, and does not guarantee that you will pass a formal CMMC assessment conducted by an accredited Certified Third-Party Assessment Organization (C3PAO).\n\nActual CMMC certification requires assessment by a C3PAO accredited by The Cyber AB. The results, scores, and documents generated by this Service are for preparation purposes only and should not be submitted to the Department of Defense (DoD) as evidence of CMMC certification.\n\nTheClearAudit makes no warranty, express or implied, that use of the Service will result in CMMC certification or compliance with any DoD requirement.`,
    },
    {
      title: '4. SPRS Score Disclaimer',
      content: `The SPRS score calculated by this Service is an estimate based on the information you provide. It is intended to help you understand your approximate compliance posture and prepare for formal assessment.\n\nThe official SPRS score that you submit to the Department of Defense must be based on a formal self-assessment conducted in accordance with NIST SP 800-171A and submitted through the official SPRS portal at sprs.csd.disa.mil.\n\nDo not submit the score generated by this Service directly to the DoD as your official SPRS score without conducting a proper formal self-assessment. Submitting inaccurate information to the DoD may have legal consequences under the False Claims Act.`,
    },
    {
      title: '5. AI Assistant Disclaimer',
      content: `The AI assistant feature of this Service is powered by artificial intelligence and is provided for informational and educational purposes only. AI-generated responses:\n\n• Are not legal advice and should not be relied upon as such\n• Are not official compliance guidance from the DoD or any government agency\n• May contain errors, inaccuracies, or outdated information\n• Should be verified against official CMMC documentation from dodcio.defense.gov\n\nAlways consult qualified cybersecurity professionals and legal counsel for specific compliance decisions.`,
    },
    {
      title: '6. User Accounts and Security',
      content: `You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account. You are responsible for all activities that occur under your account.\n\nYou must provide accurate and complete information when creating your account. You may not use another person's account without permission. We reserve the right to suspend or terminate accounts that violate these Terms.`,
    },
    {
      title: '7. Subscription and Payment',
      content: `Access to certain features of the Service requires a paid subscription. By subscribing you agree to pay the applicable fees as described on our pricing page. All fees are in US dollars.\n\nSubscriptions automatically renew unless cancelled before the renewal date. You may cancel your subscription at any time through the billing portal. Cancellations take effect at the end of the current billing period. We do not provide refunds for partial billing periods.\n\nWe reserve the right to change our pricing with 30 days notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.\n\nPayment processing is handled by Stripe Inc. We do not store your credit card information. Stripe's terms of service and privacy policy apply to payment processing.`,
    },
    {
      title: '8. One-Time Setup Fees',
      content: `Certain subscription plans include a one-time setup fee charged at the time of initial subscription. Setup fees are non-refundable once the account has been activated and access to the Service has been granted.`,
    },
    {
      title: '9. Data and Privacy',
      content: `You retain ownership of all data you enter into the Service including assessment answers, company information, and uploaded documents. By using the Service you grant us a limited license to process and store your data for the purpose of providing the Service.\n\nWe implement reasonable security measures to protect your data. However no system is completely secure and we cannot guarantee absolute security of your data.\n\nPlease review our Privacy Policy for detailed information about how we collect, use, and protect your data.`,
    },
    {
      title: '10. Acceptable Use',
      content: `You agree not to:\n\n• Use the Service for any unlawful purpose\n• Attempt to gain unauthorized access to any part of the Service\n• Upload malicious code, viruses, or harmful content\n• Interfere with or disrupt the Service or servers\n• Reverse engineer or attempt to extract source code\n• Resell or redistribute the Service without authorization\n• Use the Service to generate false compliance documentation\n• Submit AI-generated or Service-generated scores to the DoD without conducting a proper formal assessment`,
    },
    {
      title: '11. Intellectual Property',
      content: `The Service and its original content, features, and functionality are owned by ${companyName} and are protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our Service without explicit written permission.\n\nPolicy templates and documents generated by the Service are provided for your use in your compliance preparation. You may customize and use these templates for your organization's compliance activities.`,
    },
    {
      title: '12. Third-Party Services',
      content: `The Service integrates with third-party services including Stripe (payment processing), Supabase (data storage), and a third-party AI provider (AI services). Use of these third-party services is subject to their respective terms of service and privacy policies. We are not responsible for the practices of these third-party services.`,
    },
    {
      title: '13. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, THECLEARAUDIT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES INCLUDING BUT NOT LIMITED TO:\n\n• Loss of DoD contracts or business opportunities\n• Failure to achieve CMMC certification\n• Penalties or fines resulting from non-compliance\n• Data loss or security breaches\n• Reliance on AI-generated guidance\n\nOUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.`,
    },
    {
      title: '14. Disclaimer of Warranties',
      content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS. WE MAKE NO WARRANTIES REGARDING THE ACCURACY, COMPLETENESS, OR FITNESS FOR A PARTICULAR PURPOSE OF THE SERVICE OR ITS CONTENT.`,
    },
    {
      title: '15. Indemnification',
      content: `You agree to indemnify and hold harmless ${companyName} and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party.`,
    },
    {
      title: '16. Termination',
      content: `We may terminate or suspend your account and access to the Service at our sole discretion, with or without cause, with or without notice. Upon termination your right to use the Service will immediately cease. You may export your data before termination. We will retain your data for 30 days after termination before permanent deletion.`,
    },
    {
      title: '17. Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of the State of Maryland, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved in the courts of Maryland.`,
    },
    {
      title: '18. Changes to Terms',
      content: `We reserve the right to modify these Terms at any time. We will notify you of material changes by email or by posting a notice on the Service. Your continued use of the Service after changes are posted constitutes acceptance of the modified Terms.`,
    },
    {
      title: '19. Contact Information',
      content: `If you have questions about these Terms please contact us at:\n\n${companyName}\nEmail: ${companyEmail}\nWebsite: ${websiteUrl}`,
    },
  ];

  return (
    <div style={{
      height: '100vh', background: 'var(--bg)',
      fontFamily: 'var(--font)', color: 'var(--text)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: '#fff',
          }}>CM</div>
          <span style={{ fontSize: 15, fontWeight: 600 }}>TheClearAudit</span>
        </div>
        {onBack && (
          <span
            onClick={onBack}
            style={{
              fontSize: 13, color: 'var(--accent)',
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            Back
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 40px 80px' }}>

        {/* Title */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: 'var(--text)',
            marginBottom: 10, letterSpacing: '-0.01em',
          }}>
            Terms of Service
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            Last updated: {lastUpdated}
          </div>
        </div>

        {/* Important Notice */}
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          borderRadius: 10, padding: 20, marginBottom: 32,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: 'var(--amber)', marginBottom: 8,
          }}>
            Important Notice
          </div>
          <div style={{ fontSize: 13, color: 'var(--amber)', lineHeight: 1.6 }}>
            TheClearAudit is a compliance preparation tool, not an official CMMC assessment or certification service. Using this platform does not certify your organization or guarantee compliance with DoD requirements. Please read Section 3 carefully.
          </div>
        </div>

        {/* Sections */}
        {sections.map((section, i) => (
          <div key={i} style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 16, fontWeight: 600,
              marginBottom: 12,
              color: section.title.includes('IMPORTANT') ? 'var(--red)' : 'var(--text)',
            }}>
              {section.title}
            </h2>
            <div style={{
              fontSize: 13, color: 'var(--text2)', lineHeight: 1.8,
              whiteSpace: 'pre-line',
            }}>
              {section.content}
            </div>
            {i < sections.length - 1 && (
              <div style={{
                marginTop: 32, borderBottom: '1px solid var(--border)',
              }} />
            )}
          </div>
        ))}

        {/* Footer */}
        <div style={{
          marginTop: 48, padding: '20px 24px',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, textAlign: 'center',
          fontSize: 12, color: 'var(--text3)', lineHeight: 1.6,
        }}>
          By using TheClearAudit you acknowledge that you have read, understood, and agree to these Terms of Service.
          <br />
          Questions? Contact us at <span style={{ color: 'var(--accent)' }}>{companyEmail}</span>
        </div>
      </div>
    </div>
  );
}